import React from 'react'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { Alert, message, Progress, Spin } from 'antd-v4'
import { Icon, Upload } from 'antd'
import { UploadFile } from 'antd/lib/upload/interface'
import { UploadChangeParam } from 'antd-v4/lib/upload'
import { parseErrorMsg, sleep, uploadFileToS3Bucket } from '../../../../../utilities/helpers'
import { isArray } from 'lodash'
import { CloseCircleFilled } from '@ant-design/icons'
import { ICustomerImportContract } from '@getgreenline/customers'

const totalProgressSteps = 4

const csvFileExt = 'csv'

const baseDraggerStyle = {
  margin: '18px 0px',
}

interface Props {
  isModalOpen: boolean
  hasPermission: boolean
  permissionErrorMessage: string
  onImport: (companyId: number, { fileName }: ICustomerImportContract) => void
  loadCustomers: () => Promise<void>
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface IFileObject {
  originFileObj: File | Blob
  fullFileName: string
  fileExtension: string
  contentType: string
}

interface State {
  isUploading: boolean
  fileObject?: IFileObject
  uploadStepsTaken: number
  progressMessage: string
  showSuccessMessage: boolean
  errorMessages: string[]
}

@inject('currentCompanyStore')
@observer
export class ImportDropzone extends React.Component<Props & InjectedProps, State> {
  readonly company = this.props.currentCompanyStore!.company!

  constructor(props: Props & InjectedProps) {
    super(props)

    this.state = {
      isUploading: false,
      uploadStepsTaken: 0,
      progressMessage: '',
      showSuccessMessage: false,
      errorMessages: [],
    }
  }

  get progressRate() {
    const { uploadStepsTaken } = this.state
    return Math.floor((uploadStepsTaken / totalProgressSteps) * 100)
  }

  componentDidUpdate(prevProps: Props & InjectedProps, prevState: State) {
    const { isModalOpen } = this.props

    if (prevProps.isModalOpen && !isModalOpen) {
      this.setState({
        showSuccessMessage: false,
        errorMessages: [],
      })
    }
  }

  finishUploading = () =>
    this.setState({
      isUploading: false,
      uploadStepsTaken: 0,
    })

  onDrop = async (file: UploadFile<any>) => {
    try {
      this.setState({
        showSuccessMessage: false,
        errorMessages: [],
        isUploading: true,
        progressMessage: 'Uploading file...',
        uploadStepsTaken: this.state.uploadStepsTaken + 1,
      })

      await this.validateDroppedFile(file)
      await this.uploadFile()
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while handling the file', 10)
      this.finishUploading()
    }
  }

  validateDroppedFile = async (file: UploadFile<any>) => {
    const { name, type, originFileObj } = file
    const fileExtension = name.split('.').pop()

    if (!fileExtension || fileExtension !== csvFileExt) {
      this.showFileTypeErrorMessage()
      this.finishUploading()
      return
    }

    if (!originFileObj) {
      this.finishUploading()
      throw new Error(`The originFileObj of ${name} is unavailable.`)
    }

    this.setState({
      fileObject: {
        originFileObj,
        fullFileName: name,
        fileExtension,
        contentType: type,
      },
    })
  }

  showFileTypeErrorMessage = () => {
    message.error(<span>Please upload a file with '.csv' extension</span>, 10)
  }

  uploadFile = async () => {
    if (!this.state.fileObject) {
      this.finishUploading()
      return
    }

    const { originFileObj, fileExtension, contentType } = this.state.fileObject

    const { fileName, signedUrl } =
      await this.props.currentCompanyStore!.getSignedTempFileUploadUrl(fileExtension, contentType)

    this.setState({
      progressMessage: 'Uploading file...',
      uploadStepsTaken: this.state.uploadStepsTaken + 1,
    })

    this.handleS3Upload(originFileObj, fileName, signedUrl, contentType)
  }

  handleS3Upload = (
    file: File | Blob,
    fileName: string,
    signedUrl: string,
    contentType: string,
  ): void => {
    const xhr = uploadFileToS3Bucket(file, signedUrl, contentType)

    xhr.onload = async () => {
      if (xhr.status !== 200) {
        this.finishUploading()
        return message.error('An error occurred while uploading your file')
      }

      await this.xhrOnload(fileName)
    }
  }

  xhrOnload = async (fileName: string) => {
    const { onImport, loadCustomers } = this.props

    try {
      this.setState({
        progressMessage: 'Uploading file...',
        uploadStepsTaken: this.state.uploadStepsTaken + 1,
      })

      await sleep(500) // to show the progress bar properly

      await onImport(this.company.id, { fileName })

      message.success(`Successfully imported`, 5)

      this.setState({
        progressMessage: 'Uploading file...',
        uploadStepsTaken: this.state.uploadStepsTaken + 1,
        showSuccessMessage: true,
      })

      await sleep(2000) // show the completed bar for 2 seconds
      this.finishUploading()
      await loadCustomers()
    } catch (error: any) {
      const errorMessages = error?.response?.data?.message
      if (isArray(errorMessages)) {
        this.setState({ errorMessages })
      } else {
        const errorMessage = parseErrorMsg(error) || 'An error occurred while processing your file'
        message.error(errorMessage, 10)
      }
      this.finishUploading()
    }
  }

  showErrorMessages = () => {
    const { errorMessages } = this.state

    return (
      <Alert
        type='error'
        message={
          <div className='d-flex align-items-center'>
            <div>
              <CloseCircleFilled style={{ color: 'red' }} />
            </div>
            <div className='ml-2'>
              <div>{errorMessages[0]}</div>
              <ul className='pl-4 mb-0'>
                {errorMessages.slice(1).map((msg, index) => (
                  <li key={index}>{msg}</li>
                ))}
              </ul>
            </div>
          </div>
        }
      />
    )
  }

  render() {
    const { isUploading, progressMessage, errorMessages, showSuccessMessage } = this.state
    const { hasPermission, permissionErrorMessage } = this.props

    if (isUploading) {
      return (
        <div style={{ marginTop: '50px' }}>
          <Progress percent={this.progressRate} />
          <div className='d-flex align-items-center justify-content-center mt-2'>
            <span className='mr-2'>{progressMessage}</span>
            <Spin className='mt-1' size='small' />
          </div>
        </div>
      )
    }

    return (
      <div className='mt-3'>
        <Upload.Dragger
          name='file'
          multiple={false}
          onChange={(info: UploadChangeParam<UploadFile<any>>) => this.onDrop(info.file)}
          style={
            hasPermission
              ? baseDraggerStyle
              : { ...baseDraggerStyle, backgroundColor: 'rgba(0, 0, 0, 0.1)', opacity: 0.7 }
          }
          accept={`.${csvFileExt}`}
          showUploadList={false}
          className='mt-2'
          disabled={!hasPermission}
        >
          <p className='ant-upload-drag-icon'>
            <Icon type='inbox' />
          </p>
          <div className='ant-upload-text'>
            {hasPermission ? 'Click or drag file to upload' : permissionErrorMessage}
          </div>
        </Upload.Dragger>

        {errorMessages.length > 0 && this.showErrorMessages()}

        {showSuccessMessage && (
          <Alert
            message={<div className='text-center'>File successfully imported.</div>}
            showIcon
            type='success'
          />
        )}
      </div>
    )
  }
}
